/**
 * Viewport breakpoints, from DESIGN.md section 12.
 *
 * These are the four widths FND-11 is judged at, plus the 1600 content-cap
 * step. They exist in TypeScript as well as CSS because two behaviors are
 * structural rather than cosmetic and cannot be done in a media query:
 *
 *   - the navigation changes *shape* (bottom tabs / rail / labeled rail), and
 *   - `ADM-*` grids are unsupported below 768 and must deep-link out rather
 *     than render a broken table.
 */

import { onScopeDispose, readonly, ref, computed, type ComputedRef, type Ref } from 'vue'

export const BREAKPOINTS = {
  /** Small phone. Search results become a card list; admin grids are refused. */
  phone: 360,
  /** Tablet. Collapsible icon rail; admin grids get horizontal scroll. */
  tablet: 768,
  /** Persistent rail. */
  desktop: 1024,
  /** The design target. Persistent rail with labels, plus the filter panel. */
  target: 1280,
  /** Content stops growing; rows must not stretch. */
  wide: 1600
} as const

export type BreakpointName = keyof typeof BREAKPOINTS

/**
 * The floor for any `ADM-*` grid. DESIGN.md section 12: "explicitly not
 * supported below 768" — CSV export and bulk operations on a phone are a false
 * promise, so the honest response is to say so and offer the desktop link.
 */
export const ADMIN_GRID_MIN_WIDTH = BREAKPOINTS.tablet

function currentWidth (): number {
  if (typeof window === 'undefined') return BREAKPOINTS.target
  return window.innerWidth
}

export interface ViewportState {
  width: Readonly<Ref<number>>
  /** The largest named breakpoint the viewport has reached. */
  name: ComputedRef<BreakpointName>
  atLeast: (breakpoint: BreakpointName) => boolean
  below: (breakpoint: BreakpointName) => boolean
}

/**
 * Reactive viewport width.
 *
 * `resize` rather than a set of `matchMedia` listeners: the component needs the
 * width for more than one threshold, and one listener that reads `innerWidth`
 * is less state than five media queries kept in sync with the same numbers.
 */
export function useViewport (): ViewportState {
  const width = ref(currentWidth())

  if (typeof window !== 'undefined') {
    const onResize = (): void => {
      width.value = currentWidth()
    }
    window.addEventListener('resize', onResize, { passive: true })
    onScopeDispose(() => window.removeEventListener('resize', onResize))
  }

  const name = computed<BreakpointName>(() => {
    const w = width.value
    if (w >= BREAKPOINTS.wide) return 'wide'
    if (w >= BREAKPOINTS.target) return 'target'
    if (w >= BREAKPOINTS.desktop) return 'desktop'
    if (w >= BREAKPOINTS.tablet) return 'tablet'
    return 'phone'
  })

  return {
    width: readonly(width),
    name,
    atLeast: (breakpoint) => width.value >= BREAKPOINTS[breakpoint],
    below: (breakpoint) => width.value < BREAKPOINTS[breakpoint]
  }
}
